import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioChannelExtra,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x41
const BLOCK_SIZE = 32
const CHANNEL_COUNT = 199
const CHANNEL_START = 0x10
const CHANNEL_SIZE = 16
const NAME_START = 0xd40
const NAME_SIZE = 8
const USED_START = 0x1900
const SCAN_START = 0x1920
const POWER_MASK = 0x0c
const NARROW_MASK = 0x10
const AIRBAND_START = 108_000_000
const AIRBAND_END = 136_999_999
const MEMORY_SIZE = 0x10000
const PROGRAM_COMMAND = Uint8Array.of(0x02, 0x50, 0x52, 0x4f, 0x47, 0x52, 0x41)
const PROGRAM_COMMAND_2 = Uint8Array.of(0x4d, 0x02)

type TIDHidSpec = {
  id: string
  model: string
  summary: string
  identity: string
  powerLevels: readonly string[]
}

export type TIDHidProfile = {
  protocol: 'tid-hid'
  id: string
  vendor: 'TIDRADIO'
  model: string
  summary: string
  tags: readonly string[]
  channelMaximum: number
  nameLength: 8
  memorySize: number
  identity: readonly number[]
  powerLevels: readonly string[]
  readOnly: boolean
  writeEnabled: boolean
}

function spec(
  family: string,
  mode: 'Normal' | 'Ham' | 'GMRS',
  identityHex: string,
): TIDHidSpec {
  const model = `${family}-${mode === 'Ham' ? 'HAM' : mode}`
  return {
    id: `tidradio-${family.toLowerCase()}-${mode.toLowerCase()}`,
    model,
    summary: `TIDRADIO ${family} ${mode} color-screen radio from the 2026-04-29 CPS family.`,
    identity: identityHex,
    powerLevels:
      mode === 'GMRS' ? ['Low', 'High'] : ['Low', 'Middle', 'High'],
  }
}

const SPECS: readonly TIDHidSpec[] = [
  spec('TD-H3-Plus', 'Normal', '503331313833FFFF'),
  spec('TD-H3-Plus', 'Ham', '503331313835FFFF'),
  spec('TD-H3-Plus', 'GMRS', '503331313834FFFF'),
  spec('TD-H4', 'Normal', '54444834FFFFFF4E'),
  spec('TD-H4', 'Ham', '54444834FFFFFF48'),
  spec('TD-H4', 'GMRS', '54444834FFFFFF47'),
  spec('TD-H7-Plus', 'Normal', '54444837504C554E'),
  spec('TD-H7-Plus', 'Ham', '54444837504C5548'),
  spec('TD-H7-Plus', 'GMRS', '54444837504C5547'),
  spec('TD-H8-4th-Gen', 'Normal', '544448383447454E'),
  spec('TD-H8-4th-Gen', 'Ham', '5444483834474548'),
  spec('TD-H8-4th-Gen', 'GMRS', '5444483834474547'),
  spec('TD-H9', 'Normal', '54444839FFFFFF4E'),
  spec('TD-H9', 'Ham', '54444839FFFFFF48'),
  spec('TD-H9', 'GMRS', '54444839FFFFFF47'),
  spec('TD-638UV', 'Normal', '544436333855564E'),
  spec('TD-638UV', 'Ham', '5444363338555648'),
  spec('TD-638UV', 'GMRS', '5444363338555647'),
]

export const TID_HID_PROFILES: readonly TIDHidProfile[] = SPECS.map((entry) => ({
  protocol: 'tid-hid',
  id: entry.id,
  vendor: 'TIDRADIO',
  model: entry.model,
  summary: entry.summary,
  tags: ['Experimental', 'HID read-only', '199 channels', 'CPS reverse-engineered'],
  channelMaximum: CHANNEL_COUNT,
  nameLength: 8,
  memorySize: MEMORY_SIZE,
  identity: Array.from(
    entry.identity.match(/.{2}/g) ?? [],
    (value) => Number.parseInt(value, 16),
  ),
  powerLevels: entry.powerLevels,
  readOnly: true,
  writeEnabled: false,
}))

function emptyChannel(profile: TIDHidProfile, number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: profile.powerLevels[0],
    skip: false,
    extras: [
      {
        name: 'pttid',
        label: 'PTT ID',
        kind: 'choice',
        choices: ['Off', 'BOT', 'EOT', 'Both'],
        value: 'Off',
      },
      {
        name: 'freqHop',
        label: 'Frequency hop',
        kind: 'boolean',
        value: false,
      },
      {
        name: 'busyLock',
        label: 'Busy-channel lockout',
        kind: 'boolean',
        value: false,
      },
    ],
  }
}

function channelOffset(number: number): number {
  return CHANNEL_START + (number - 1) * CHANNEL_SIZE
}

function nameOffset(number: number): number {
  return NAME_START + (number - 1) * NAME_SIZE
}

function bitSet(image: Uint8Array, start: number, number: number): boolean {
  const index = number - 1
  return (image[start + (index >> 3)] & (1 << (index & 7))) !== 0
}

function setBit(
  image: Uint8Array,
  start: number,
  number: number,
  enabled: boolean,
): void {
  const index = number - 1
  const offset = start + (index >> 3)
  const bit = 1 << (index & 7)
  image[offset] = enabled ? image[offset] | bit : image[offset] & ~bit
}

function decodeText(raw: Uint8Array): string {
  let end = raw.findIndex((value) => value === 0 || value === 0xff)
  if (end < 0) end = raw.byteLength
  return String.fromCharCode(...raw.slice(0, end)).trim()
}

function decodeTone(raw: Uint8Array): Pick<
  WebChannel,
  'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'
> {
  const value = raw[0] | (raw[1] << 8)
  if (value === 0 || value === 0xffff) {
    return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
  }
  if (value >= 0x8000) {
    const digits = (value & 0x3fff).toString(16).padStart(4, '0')
    return {
      toneMode: 'dtcs',
      ctcss: null,
      dtcs: Number(digits.slice(-3)),
      dtcsPolarity: (value & 0x4000) !== 0 ? 'R' : 'N',
    }
  }
  const digits = value.toString(16).padStart(4, '0')
  return {
    toneMode: /^\d{4}$/.test(digits) ? 'ctcss' : 'none',
    ctcss: /^\d{4}$/.test(digits) ? Number(digits) / 10 : null,
    dtcs: null,
    dtcsPolarity: 'N',
  }
}

function encodeTone(channel: WebChannel): Uint8Array {
  let value = 0xffff
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    if (!CTCSS_TONES.includes(channel.ctcss as (typeof CTCSS_TONES)[number])) {
      throw new Error('That CTCSS tone is not supported by this radio.')
    }
    value = Number(String(Math.round(channel.ctcss * 10)).padStart(4, '0'))
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    if (!DTCS_CODES.includes(channel.dtcs as (typeof DTCS_CODES)[number])) {
      throw new Error('That DTCS code is not supported by this radio.')
    }
    value =
      0x8000 |
      parseInt(String(channel.dtcs).padStart(3, '0'), 16) |
      (channel.dtcsPolarity === 'R' ? 0x4000 : 0)
  }
  return Uint8Array.of(value & 0xff, (value >> 8) & 0xff)
}

function extraValue(
  extras: readonly RadioChannelExtra[] | undefined,
  name: string,
): string | number | boolean | undefined {
  return extras?.find((extra) => extra.name === name)?.value
}

export function parseTIDHidChannel(
  image: Uint8Array,
  profile: TIDHidProfile,
  number: number,
): WebChannel {
  if (number < 1 || number > CHANNEL_COUNT) {
    throw new Error(`Channel must be between 1 and ${CHANNEL_COUNT}.`)
  }
  const offset = channelOffset(number)
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  const rxFrequency = decodeLbcd(raw.subarray(0, 4))
  if (
    raw.byteLength !== CHANNEL_SIZE ||
    raw.subarray(0, 4).every((value) => value === 0xff) ||
    !rxFrequency
  ) {
    return emptyChannel(profile, number)
  }
  const txFrequency = raw.subarray(4, 8).every((value) => value === 0xff)
    ? null
    : decodeLbcd(raw.subarray(4, 8))
  const rxTone = decodeTone(raw.subarray(8, 10))
  const txTone = decodeTone(raw.subarray(10, 12))
  const selected = txTone.toneMode === 'none' ? rxTone : txTone
  const powerIndex = (raw[14] & POWER_MASK) >> 2
  const airband =
    rxFrequency >= AIRBAND_START && rxFrequency <= AIRBAND_END
  return {
    number,
    empty: false,
    name: decodeText(image.subarray(nameOffset(number), nameOffset(number) + NAME_SIZE)),
    rxFrequency,
    txFrequency,
    toneMode: selected.toneMode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.dtcsPolarity,
    mode: airband ? 'AM' : raw[14] & NARROW_MASK ? 'NFM' : 'FM',
    power: profile.powerLevels[powerIndex] ?? profile.powerLevels[0],
    skip: !bitSet(image, SCAN_START, number),
    extras: [
      {
        name: 'pttid',
        label: 'PTT ID',
        kind: 'choice',
        choices: ['Off', 'BOT', 'EOT', 'Both'],
        value: ['Off', 'BOT', 'EOT', 'Both'][raw[13] & 0x03] ?? 'Off',
      },
      {
        name: 'freqHop',
        label: 'Frequency hop',
        kind: 'boolean',
        value: Boolean(raw[13] & 0x04),
      },
      {
        name: 'busyLock',
        label: 'Busy-channel lockout',
        kind: 'boolean',
        value: Boolean(raw[13] & 0x20),
      },
    ],
  }
}

export function parseTIDHidChannels(
  image: Uint8Array,
  profile: TIDHidProfile,
): WebChannel[] {
  if (image.byteLength !== profile.memorySize) {
    throw new Error(
      `This TID CPS image is ${image.byteLength.toLocaleString()} bytes; ${profile.model} requires ${profile.memorySize.toLocaleString()}.`,
    )
  }
  return Array.from({ length: CHANNEL_COUNT }, (_, index) =>
    parseTIDHidChannel(image, profile, index + 1),
  )
}

export function applyTIDHidChannel(
  image: Uint8Array,
  profile: TIDHidProfile,
  channel: WebChannel,
): void {
  const offset = channelOffset(channel.number)
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  setBit(image, USED_START, channel.number, !channel.empty)
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  if (raw.every((value) => value === 0xff)) raw.fill(0)
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  else raw.fill(0xff, 4, 8)
  raw.set(encodeTone(channel), 8)
  raw.set(encodeTone(channel), 10)
  const power = Math.max(0, profile.powerLevels.indexOf(channel.power))
  raw[14] =
    (raw[14] & ~(POWER_MASK | NARROW_MASK)) |
    ((power & 0x03) << 2) |
    (channel.mode === 'NFM' ? NARROW_MASK : 0)
  raw[13] =
    (raw[13] & 0xc0) |
    (['Off', 'BOT', 'EOT', 'Both'].indexOf(
      String(extraValue(channel.extras, 'pttid') ?? 'Off'),
    ) &
      0x03) |
    (Boolean(extraValue(channel.extras, 'freqHop')) ? 0x04 : 0) |
    (Boolean(extraValue(channel.extras, 'busyLock')) ? 0x20 : 0)
  const name = channel.name
    .toUpperCase()
    .replace(/[^\x20-\x7e]/g, '')
    .slice(0, NAME_SIZE)
  const start = nameOffset(channel.number)
  image.fill(0, start, start + NAME_SIZE)
  for (let index = 0; index < name.length; index += 1) {
    image[start + index] = name.charCodeAt(index)
  }
  setBit(image, SCAN_START, channel.number, !channel.skip)
}

async function enterProgrammingMode(
  profile: TIDHidProfile,
  transport: RadioTransport,
): Promise<void> {
  await transport.discardInput()
  await transport.write(PROGRAM_COMMAND)
  if ((await transport.readExact(1, 3000))[0] !== ACK) {
    throw new Error('The TID radio refused the HID programming command.')
  }
  await transport.write(PROGRAM_COMMAND_2)
  const identity = await transport.readExact(8, 3000)
  if (!profile.identity.every((value, index) => identity[index] === value)) {
    const reported = new TextDecoder()
      .decode(identity)
      .replace(/\u00ff/g, '')
      .trim()
    throw new Error(
      `The connected radio reports ${JSON.stringify(reported)}, not ${profile.model}.`,
    )
  }
  await transport.write(Uint8Array.of(ACK))
  if ((await transport.readExact(1, 3000))[0] !== ACK) {
    throw new Error('The TID radio rejected the HID clone session.')
  }
}

async function exitProgrammingMode(transport: RadioTransport): Promise<void> {
  await transport.write(new TextEncoder().encode('ENDR'))
}

async function readRadioBlock(
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  await transport.write(
    Uint8Array.of(
      0x52,
      (address >> 8) & 0xff,
      address & 0xff,
      BLOCK_SIZE,
    ),
  )
  const response = await transport.readExact(BLOCK_SIZE + 4, 4000)
  if (
    response[0] !== 0x57 ||
    response[1] !== ((address >> 8) & 0xff) ||
    response[2] !== (address & 0xff) ||
    response[3] !== BLOCK_SIZE
  ) {
    throw new Error(`Corrupt TID HID read at 0x${address.toString(16)}.`)
  }
  return response.slice(4)
}

async function writeRadioBlock(
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  if (data.byteLength !== BLOCK_SIZE) {
    throw new Error('TID HID writes must contain exactly 32 bytes.')
  }
  const packet = new Uint8Array(4 + BLOCK_SIZE)
  packet.set([
    0x57,
    (address >> 8) & 0xff,
    address & 0xff,
    BLOCK_SIZE,
  ])
  packet.set(data, 4)
  await transport.write(packet)
  if ((await transport.readExact(1, 4000))[0] !== ACK) {
    throw new Error(`The radio rejected TID HID write block 0x${address.toString(16)}.`)
  }
}

export async function readTIDHidRadioWithTransport(
  profile: TIDHidProfile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enterProgrammingMode(profile, transport)
  try {
    const image = new Uint8Array(profile.memorySize).fill(0xff)
    const total = Math.ceil(profile.memorySize / BLOCK_SIZE)
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      image.set(await readRadioBlock(transport, address & 0xffff), address)
      onProgress?.({
        current: index + 1,
        total,
        label: 'Reading TID HID codeplug',
      })
    }
    return { image, reportedModel: profile.model }
  } finally {
    await exitProgrammingMode(transport)
  }
}

export async function writeTIDHidRadioWithTransport(
  profile: TIDHidProfile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (!profile.writeEnabled) {
    throw new Error(
      'TID HID writes remain disabled until the first physical read/edit/write validation is recorded.',
    )
  }
  if (image.byteLength !== profile.memorySize) {
    throw new Error(
      `This TID CPS image is ${image.byteLength.toLocaleString()} bytes; ${profile.model} requires ${profile.memorySize.toLocaleString()}.`,
    )
  }
  await enterProgrammingMode(profile, transport)
  const total = Math.ceil(profile.memorySize / BLOCK_SIZE)
  for (let index = 0; index < total; index += 1) {
    const address = index * BLOCK_SIZE
    await writeRadioBlock(
      transport,
      address & 0xffff,
      image.slice(address, address + BLOCK_SIZE),
    )
    onProgress?.({
      current: index + 1,
      total,
      label: 'Writing TID HID codeplug',
    })
  }
  for (let attempt = 0; attempt < 2; attempt += 1) {
    await transport.write(new TextEncoder().encode('ENDW'))
    if ((await transport.readExact(1, 4000))[0] !== ACK) {
      throw new Error('The radio did not acknowledge the completed TID HID write.')
    }
  }
}
